<template>
  <div class="app-root">
    <router-view />
  </div>
</template>

<script>
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";
import update from "@/mixins/update";
import sync from "@/mixins/sync";
import { mapGetters, mapActions } from "vuex";
import { TokenService } from "@services";
import { Trans } from "@/plugins/Translation";
import tracer from "@/diagnostics/tracer.js";

tracer.init();

export default {
  name: "App",

  metaInfo() {
    let robots;
    if (process.env.VUE_APP_META_ROBOTS) {
      robots = process.env.VUE_APP_META_ROBOTS;
    } else {
      robots = "index,follow";
    }
    return {
      titleTemplate: "%s | Acrisure Safety",
      meta: [{ name: "robots", content: robots }],
    };
  },

  mixins: [update, sync],

  computed: {
    ...mapGetters("settings", ["settings"]),
    ...mapGetters("user", ["user"]),
    isAndroid() {
      return Capacitor.getPlatform() === "android";
    },
  },

  data() {
    return {
      pushes: null,
      pushReceived: null,
    };
  },

  methods: {
    ...mapActions("user", ["setUser"]),
    ...mapActions("auth", ["loadAccessToken"]),
    ...mapActions("notifications", ["getPushNotifications"]),
    ...mapActions("settings", ["setLang"]),
    ...mapActions("timeClock", ["getTimeClocks"]),

    async initializeStatusBar() {
      if (!Capacitor.isPluginAvailable("StatusBar")) return;

      try {
        if (this.isAndroid) {
          const isDark = this.$vuetify.theme.dark;
          const backgroundColor = isDark ? "#121212" : "#FFFFFF";

          // Set both status bar and edge-to-edge colors
          await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
          await StatusBar.setBackgroundColor({ color: backgroundColor });

          // Configure edge-to-edge support
          await EdgeToEdge.setBackgroundColor({ color: backgroundColor });
          await EdgeToEdge.setNavigationBarColor({ color: backgroundColor });
          await EdgeToEdge.setStatusBarColor({ color: backgroundColor });
          await EdgeToEdge.setNavigationBarStyle({ style: isDark ? "Dark" : "Light" });
          await EdgeToEdge.setStatusBarStyle({ style: isDark ? "Dark" : "Light" });
        }
      } catch (error) {
        console.error("Error initializing status bar:", error);
      }
    },

    async setThemeAndStatusBar(isDark) {
      this.$vuetify.theme.dark = isDark;
      await this.initializeStatusBar();
    },

    async checkNavVisibility() {
      try {
        if (this.user.navigationAccess?.planners === undefined || this.user.navigationAccess?.planners === null) {
          const apiURL = this.$store.state.api.apiURL || "";

          let env = "prod";

          if (apiURL.includes("localhost") || apiURL.includes("dev")) {
            env = "dev";
          } else if (apiURL.includes("qa")) {
            env = "qa";
          } else if (apiURL.includes("stage")) {
            env = "stage";
          }

          const timestamp = Date.now();
          const response = await fetch(
            `https://clockworksafety-public.s3.us-west-2.amazonaws.com/${env}/companyplanners.json?t=${timestamp}`,
          );
          const data = await response.json();
          const company = data.companies.find((c) => c.id == this.user?.companyId);
          if (company) {
            this.setShowPlanners(company.planner);
          }
        } else {
          this.setShowPlanners(this.user.navigationAccess.planners);
        }
      } catch (error) {
        console.error("Error checking nav visibility:", error);
      }
    },
  },

  async created() {
    if (Capacitor.isPluginAvailable("PushNotifications")) {
      this.getPushNotifications();
    }
    if (this.settings.lang) {
      Trans.changeLanguage(this.settings.lang);
    }
  },

  async mounted() {
    // Set initial theme
    const shouldBeDark = this.settings?.darkTheme ?? true;
    await this.setThemeAndStatusBar(shouldBeDark);

    if (TokenService.getToken()) {
      await this.loadAccessToken();
      await this.setUser();
      // Load active time clock state from server
      if (this.$online) {
        try {
          await this.getTimeClocks();
        } catch (error) {
          console.error("Error loading time clock state:", error);
        }
      }
    }

    if (this.$platform == "mobile") {
      this.appStateListener = Capacitor.App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          this.initializeStatusBar(); // Refresh status bar on app resume
          // Refresh time clock state when app becomes active
          if (TokenService.getToken() && this.$online) {
            this.getTimeClocks().catch((error) => {
              console.error("Error refreshing time clock state:", error);
            });
          }
        }
      });
    } else {
      this.visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          this.initializeStatusBar(); // Refresh status bar on visibility change
          // Refresh time clock state when page becomes visible
          if (TokenService.getToken() && this.$online) {
            this.getTimeClocks().catch((error) => {
              console.error("Error refreshing time clock state:", error);
            });
          }
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  },

  watch: {
    "settings.darkTheme": {
      async handler(newValue) {
        await this.setThemeAndStatusBar(newValue ?? true);
      },
    },
    "$vuetify.theme.dark": {
      async handler() {
        await this.initializeStatusBar();
      },
    },
  },

  beforeDestroy() {
    if (this.$platform == "mobile") {
      this.appStateListener?.remove();
    } else {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
  },
};
</script>

<style>
.app-root {
  min-height: 100vh;
  background-color: var(--v-background-base, #121212);
}

.v-bottom-navigation {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
